import { useEffect, useState } from 'react';
import { campaignsApi } from '@/lib/api';
import { Campaign } from '@/types';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import clsx from 'clsx';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

const statusStyles: Record<string, string> = {
  active: 'badge-success',
  paused: 'badge-warning',
  archived: 'badge-neutral',
};

export default function OrgCampaigns() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    type: 'percentage' as 'percentage' | 'flat',
    commissionValue: 10,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchCampaigns = async () => {
    try {
      const response = await campaignsApi.list();
      if (response.data.success) {
        setCampaigns(response.data.data.campaigns);
      }
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);

    try {
      const response = await campaignsApi.create({
        name: newCampaign.name,
        description: newCampaign.description || undefined,
        type: newCampaign.type,
        commissionValue: newCampaign.commissionValue,
      });

      if (response.data.success) {
        toast.success('Campaign created successfully!');
        setShowCreateModal(false);
        setNewCampaign({ name: '', description: '', type: 'percentage', commissionValue: 10 });
        fetchCampaigns();
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to create campaign');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (campaignId: string) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;

    try {
      await campaignsApi.delete(campaignId);
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch {
      toast.error('Failed to delete campaign');
    }
  };

  const openEditModal = (campaign: Campaign) => {
    setEditingCampaign({ ...campaign });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCampaign) return;
    setIsUpdating(true);

    try {
      const response = await campaignsApi.update(editingCampaign.campaignId, {
        name: editingCampaign.name,
        description: editingCampaign.description || undefined,
        type: editingCampaign.type,
        commissionValue: editingCampaign.commissionValue,
        status: editingCampaign.status,
      });

      if (response.data.success) {
        toast.success('Campaign updated successfully!');
        setShowEditModal(false);
        setEditingCampaign(null);
        fetchCampaigns();
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to update campaign');
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rust"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold text-charcoal">Campaigns</h2>
          <p className="text-charcoal/60 mt-1">Create and manage marketing campaigns</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />
          Create Campaign
        </button>
      </div>

      <div className="rounded-xl bg-warm-white shadow-sm overflow-hidden">
        {campaigns.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mx-auto mb-4">
              <PlusIcon className="w-8 h-8 text-charcoal/40" />
            </div>
            <h3 className="text-lg font-medium text-charcoal mb-2">No campaigns yet</h3>
            <p className="text-charcoal/60 mb-6">Create campaigns with custom commission rates.</p>
            <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
              Create Campaign
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream/50 border-b border-tan/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Campaign</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-tan/20">
                {campaigns.map((campaign) => (
                  <tr key={campaign.campaignId} className="hover:bg-cream/30">
                    <td className="px-6 py-4">
                      <p className="font-medium text-charcoal">{campaign.name}</p>
                      {campaign.description && (
                        <p className="text-xs text-charcoal/60">{campaign.description}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sage font-serif font-medium">
                        {campaign.type === 'percentage' ? `${campaign.commissionValue}%` : `$${campaign.commissionValue}`}
                      </span>
                      <span className="text-charcoal/50 text-xs ml-1">
                        ({campaign.type})
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx('badge', statusStyles[campaign.status])}>
                        {campaign.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-charcoal/60">
                      {format(new Date(campaign.createdAt), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditModal(campaign)}
                          className="p-2 text-charcoal/60 hover:text-charcoal hover:bg-cream rounded-lg"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(campaign.campaignId)}
                          className="p-2 text-charcoal/60 hover:text-rust hover:bg-rust/10 rounded-lg"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/60">
          <div className="rounded-xl bg-warm-white shadow-xl w-full max-w-md animate-fade-in">
            <div className="px-6 py-4 border-b border-tan/30 flex items-center justify-between">
              <h3 className="text-lg font-display font-semibold text-charcoal">Create Campaign</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-charcoal/60 hover:text-charcoal text-xl">
                &times;
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Campaign Name *</label>
                <input
                  type="text"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                  className="input"
                  placeholder="Summer Sale 2024"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Description</label>
                <input
                  type="text"
                  value={newCampaign.description}
                  onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                  className="input"
                  placeholder="Optional description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Commission Type</label>
                <select
                  value={newCampaign.type}
                  onChange={(e) => setNewCampaign({ ...newCampaign, type: e.target.value as 'percentage' | 'flat' })}
                  className="input"
                >
                  <option value="percentage">Percentage</option>
                  <option value="flat">Flat Amount</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">
                  Commission Value {newCampaign.type === 'percentage' ? '(%)' : '($)'}
                </label>
                <input
                  type="number"
                  value={newCampaign.commissionValue}
                  onChange={(e) => setNewCampaign({ ...newCampaign, commissionValue: Number(e.target.value) })}
                  className="input"
                  min="0"
                  step={newCampaign.type === 'percentage' ? '0.1' : '0.01'}
                  required
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-secondary flex-1">
                  Cancel
                </button>
                <button type="submit" disabled={isCreating} className="btn btn-primary flex-1">
                  {isCreating ? 'Creating...' : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingCampaign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/60">
          <div className="rounded-xl bg-warm-white shadow-xl w-full max-w-md animate-fade-in">
            <div className="px-6 py-4 border-b border-tan/30 flex items-center justify-between">
              <h3 className="text-lg font-display font-semibold text-charcoal">Edit Campaign</h3>
              <button onClick={() => setShowEditModal(false)} className="text-charcoal/60 hover:text-charcoal text-xl">
                &times;
              </button>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Campaign Name *</label>
                <input
                  type="text"
                  value={editingCampaign.name}
                  onChange={(e) => setEditingCampaign({ ...editingCampaign, name: e.target.value })}
                  className="input"
                  placeholder="Summer Sale 2024"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Description</label>
                <input
                  type="text"
                  value={editingCampaign.description || ''}
                  onChange={(e) => setEditingCampaign({ ...editingCampaign, description: e.target.value })}
                  className="input"
                  placeholder="Optional description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Commission Type</label>
                <select
                  value={editingCampaign.type}
                  onChange={(e) => setEditingCampaign({ ...editingCampaign, type: e.target.value as 'percentage' | 'flat' | 'tiered' })}
                  className="input"
                >
                  <option value="percentage">Percentage</option>
                  <option value="flat">Flat Amount</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">
                  Commission Value {editingCampaign.type === 'percentage' ? '(%)' : '($)'}
                </label>
                <input
                  type="number"
                  value={editingCampaign.commissionValue}
                  onChange={(e) => setEditingCampaign({ ...editingCampaign, commissionValue: Number(e.target.value) })}
                  className="input"
                  min="0"
                  step={editingCampaign.type === 'percentage' ? '0.1' : '0.01'}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Status</label>
                <select
                  value={editingCampaign.status}
                  onChange={(e) => setEditingCampaign({ ...editingCampaign, status: e.target.value as 'active' | 'paused' | 'archived' })}
                  className="input"
                >
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowEditModal(false)} className="btn btn-secondary flex-1">
                  Cancel
                </button>
                <button type="submit" disabled={isUpdating} className="btn btn-primary flex-1">
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
